import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../contexts/FamilyContext'
import { useAuth } from '../contexts/AuthContext'
import Layout from '../components/layout/Layout'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import TaskCard from '../components/tasks/TaskCard'
import RewardCard from '../components/rewards/RewardCard'
import ProgressBar from '../components/ui/ProgressBar'
import Badge from '../components/ui/Badge'
import Avatar from '../components/ui/Avatar'
import Card from '../components/ui/Card'

const { FiStar, FiGift, FiTrophy, FiHeart } = FiIcons

const ChildView = () => {
  const { tasks, rewards, getChildPoints, getChildLevel, completions } = useFamily()
  const { family } = useAuth()
  const [selectedChild, setSelectedChild] = useState(family?.children?.[0]?.id || null)

  const child = family?.children?.find(c => c.id === selectedChild)
  const childPoints = child ? getChildPoints(child.id) : 0
  const level = getChildLevel(childPoints)
  
  const availableTasks = tasks.filter(task => 
    !task.assigned_to || task.assigned_to === selectedChild
  )

  const todayCompletions = completions.filter(c => {
    const today = new Date().toDateString()
    return c.child_id === selectedChild && 
           new Date(c.completed_at).toDateString() === today
  })

  const handleRewardRedeem = async (reward) => {
    console.log('Redeeming reward:', reward)
  }

  if (!child) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
          <h2 className="text-2xl font-bold text-neutral-text mb-4">Select a Child</h2>
          <p className="text-neutral-600 mb-8">Choose which child's adventure you'd like to view</p>
          
          {family?.children && family.children.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
              {family.children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => setSelectedChild(child.id)}
                  className="p-6 bg-white rounded-xl border-2 border-neutral-light hover:border-primary-300 transition-colors shadow-lg hover:shadow-xl"
                >
                  <div className="text-4xl mb-2">{child.avatar || '👦'}</div>
                  <div className="font-semibold text-neutral-text">{child.name}</div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-neutral-600">No children have been added to your family yet.</p>
          )}
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-highlight-50 via-accent-50 to-primary-50 relative overflow-hidden">
        {/* Floating background elements */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-3xl"
              initial={{ 
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
                y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
              }}
              animate={{ 
                y: [null, -20, 0],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 6 + Math.random() * 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 3
              }}
            >
              {['🌟', '⭐', '🎈', '🦄', '🌈', '✨'][Math.floor(Math.random() * 6)]}
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
          {/* Child Selector */}
          {family?.children && family.children.length > 1 && (
            <div className="flex justify-center mb-6">
              <div className="flex gap-2 p-2 bg-white rounded-xl shadow-lg">
                {family.children.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedChild(c.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                      ${selectedChild === c.id
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                        : 'text-neutral-600 hover:bg-neutral-100'
                      }
                    `}
                  >
                    <span className="text-lg">{c.avatar || '👦'}</span>
                    <span className="font-medium">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="relative inline-block">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Avatar 
                  name={child.name}
                  emoji={child.avatar || '👦'}
                  size="xl"
                  className="mb-4"
                />
              </motion.div>
              <div className="absolute -top-2 -right-2">
                <Badge variant="secondary" className="text-xs font-bold shadow-lg">
                  Level {level.level}
                </Badge>
              </div>
            </div>
            
            <h1 className="text-4xl font-bold text-neutral-text mb-2">
              Welcome back, {child.name}! 🌟
            </h1>
            <p className="text-xl text-neutral-600 mb-4">
              {level.title} • Ready for your next adventure?
            </p>
            
            <div className="max-w-md mx-auto">
              <ProgressBar
                value={childPoints}
                max={level.next}
                color="primary"
                size="lg"
                className="mb-2"
              />
              <p className="text-sm text-neutral-600">
                {level.next - childPoints} points until Level {level.level + 1}!
              </p>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card className="text-center bg-gradient-to-br from-button-primary to-accent-500 text-white shadow-xl">
                <SafeIcon icon={FiStar} className="text-3xl mb-2 mx-auto" />
                <div className="text-2xl font-bold">{childPoints}</div>
                <div className="text-sm opacity-90">Total Points</div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card className="text-center bg-gradient-to-br from-success-400 to-highlight-500 text-white shadow-xl">
                <SafeIcon icon={FiTrophy} className="text-3xl mb-2 mx-auto" />
                <div className="text-2xl font-bold">{todayCompletions.length}</div>
                <div className="text-sm opacity-90">Completed Today</div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card className="text-center bg-gradient-to-br from-primary-400 to-secondary-500 text-white shadow-xl">
                <SafeIcon icon={FiGift} className="text-3xl mb-2 mx-auto" />
                <div className="text-2xl font-bold">
                  {rewards.filter(r => childPoints >= r.points_required).length}
                </div>
                <div className="text-sm opacity-90">Available Rewards</div>
              </Card>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Today's Quests */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiStar} className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-neutral-text">Today's Quests</h2>
                  <p className="text-neutral-600">Complete tasks to earn points!</p>
                </div>
              </div>

              {availableTasks.length > 0 ? (
                <div className="space-y-4">
                  {availableTasks.slice(0, 6).map((task, index) => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <TaskCard task={task} child={child} view="child" />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="text-center py-12">
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-xl font-semibold text-neutral-text mb-2">
                    All caught up!
                  </h3>
                  <p className="text-neutral-600">
                    Great job! Check back later for new quests.
                  </p>
                </Card>
              )}
            </div>

            {/* Prize Shop */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-highlight-500 rounded-xl flex items-center justify-center shadow-lg">
                  <SafeIcon icon={FiGift} className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-neutral-text">Prize Shop</h2>
                  <p className="text-neutral-600">Spend your points on awesome rewards!</p>
                </div>
              </div>

              {rewards.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {rewards.slice(0, 6).map((reward, index) => (
                    <motion.div
                      key={reward.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <RewardCard
                        reward={reward}
                        childPoints={childPoints}
                        onRedeem={handleRewardRedeem}
                        canRedeem={true}
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="text-center py-12">
                  <div className="text-6xl mb-4">🎁</div>
                  <h3 className="text-xl font-semibold text-neutral-text mb-2">
                    No rewards yet
                  </h3>
                  <p className="text-neutral-600">
                    Ask your parents to add some exciting rewards!
                  </p>
                </Card>
              )}
            </div>
          </div>

          {/* Recent Achievements */}
          {todayCompletions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12"
            >
              <Card className="bg-gradient-to-r from-success-500 to-highlight-600 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiHeart} className="text-2xl" />
                  <h3 className="text-xl font-bold">Today's Achievements</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {todayCompletions.map((completion, index) => (
                    <motion.div
                      key={completion.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      className="bg-white/20 rounded-lg p-4 text-center"
                    >
                      <div className="text-2xl mb-2">🎉</div>
                      <div className="font-medium text-sm">
                        {completion.tasks?.title}
                      </div>
                      <div className="text-xs opacity-90">
                        +{completion.points_earned} points
                      </div>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default ChildView